import { useCallback, useState } from 'react';
import { api, errMsg } from '../api.js';
import { toast } from '../components/Toasts.jsx';

// T270 — a collapsed panel that fetches the first time it is opened.
//
// The dashboard grew two of these — Dismissed and Archived — and the second
// arrived with the comment "same shape as the Dismissed panel above and for the
// same reason", which is the moment to extract rather than copy. They are kept
// out of the main `recs` deliberately: these rows must not leak into the
// available list or into the facet counts derived from it.
//
// The one non-obvious rule, from T249: flip `loaded` on FAILURE too. Leaving it
// false pins the panel on "Loading…" for the rest of the session with only a
// faded toast to say why, and the caller's empty state — which tells the truth
// — never renders.
//
// T274 — and it pages. Both panels asked for `limit=100`, which is the route's
// hard maximum, while their own toggles carried the real count from the facets:
// the Archived button said 378 and the panel could hold 100, so 278 rows were
// unreachable by any route through the UI. This is the same defect the main
// list had before the window fix — a list that stops at its limit and says
// nothing reads as "that is all of them".
export function useOnDemandList(path, { errorLabel = 'Could not load that list' } = {}) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);

  // `path` already carries a query string in both call sites; keep appending.
  const at = useCallback((offset) => (offset ? `${path}&offset=${offset}` : path), [path]);

  const load = useCallback(async () => {
    try {
      const r = await api.get(at(0));
      setRows(r.recommendations ?? []);
      setHasMore(!!r.hasMore);
      setTotal(r.total ?? 0);
    } catch (e) {
      toast(errMsg(e, errorLabel), { kind: 'error' });
    } finally {
      setLoaded(true);
    }
  }, [at, errorLabel]);

  const loadMore = useCallback(async () => {
    if (loadingMore) return;
    setLoadingMore(true);
    try {
      // Offset comes from what we hold, not from a page counter: a restore
      // between pages changes the size of the set under us, and counting pages
      // would then skip or repeat rows.
      const r = await api.get(at(rows.length));
      const next = r.recommendations ?? [];
      // Server-side paging over a set that can shift; drop anything we already
      // hold rather than render a duplicate key.
      setRows((prev) => {
        const seen = new Set(prev.map((x) => x.id ?? x._id));
        return [...prev, ...next.filter((x) => !seen.has(x.id ?? x._id))];
      });
      setHasMore(!!r.hasMore);
      setTotal(r.total ?? 0);
    } catch (e) {
      toast(errMsg(e, errorLabel), { kind: 'error' });
    } finally {
      setLoadingMore(false);
    }
  }, [at, rows.length, loadingMore, errorLabel]);

  // T290 — REOPENING REFETCHES. `loaded` was set once and never reset, so the
  // second and every later open just flipped visibility over a frozen snapshot.
  //
  // Concretely: the operator opens Dismissed (358), closes it, dismisses five
  // more rows during triage — the toggle beside the panel now reads 363, because
  // that count comes live from the facets — and reopens to undo one of the five.
  // The panel still says 358, still says "100 of 358", and none of the five is
  // in it or reachable by Load more, which pages by offset into a set that has
  // since shifted. The panel is the ONLY route to a dismissed row once the Undo
  // toast has expired, so the row he wants is unreachable.
  //
  // The same latch meant the Archived panel never picked up rows a background
  // scan archived while the page stayed open.
  const toggle = useCallback(async () => {
    if (!open) await load();
    setOpen((v) => !v);
  }, [open, load]);

  return { open, rows, loaded, toggle, reload: load, loadMore, hasMore, total, loadingMore };
}
